import type { FC, FormEvent, ReactNode } from 'react'
import type { Article, ArticleComment } from '@/types'

import Link from 'next/link'
import { useRef, useState } from 'react'

import {
    CircleCheck,
    MessageSquare,
    MessageSquareText,
    PencilLine,
    Reply,
    Send,
    ThumbsDown,
    ThumbsUp
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { formatJalali, formatNumber, toFaDigits } from '@/lib/format'
import { usePersistentState } from '@/hooks/use-persistent-state'
import { useToast } from '@/components/generals/providers'
import { ConfluenceHint } from '@/components/generals/confluence-hint'
import { Avatar } from '@/components/ds/avatar'
import { Button } from '@/components/ds/button'
import { cardStyles } from '@/components/ds/card'
import { Callout, EmptyState } from '@/components/ds/feedback'
import { Check, Field, Select, Textarea } from '@/components/ds/field'
import { CURRENT_USER, PERSON_BY_SLUG } from '@/constants/people'

type FeedbackEntry = {
    value: 'yes' | 'no'
    reasons?: string[]
    note?: string
    sent?: boolean
}

const NEGATIVE_REASONS = [
    'اطلاعات قدیمی است',
    'توضیحات کافی نیست',
    'نمونه کاربردی ندارد',
    'پیدا کردن پاسخ دشوار بود'
]

interface ArticleFeedbackProps {
    article: Article
}

export const ArticleFeedback: FC<ArticleFeedbackProps> = ({ article }): ReactNode => {
    const [store, setStore, ready] = usePersistentState<Record<string, FeedbackEntry>>('km-feedback', {})
    const [reasons, setReasons] = useState<string[]>([])
    const [note, setNote] = useState('')
    const { notify } = useToast()
    const entry = ready ? store[article.slug] : undefined
    const helpfulCount = article.likes + (entry?.value === 'yes' ? 1 : 0)

    const save = (value: FeedbackEntry | null) => {
        setStore(current => {
            const next = { ...current }

            if (value) next[article.slug] = value
            else delete next[article.slug]

            return next
        })
    }

    const sendNegative = (event: FormEvent) => {
        event.preventDefault()
        save({ value: 'no', reasons, note: note.trim(), sent: true })
        notify('بازخورد شما برای مالک دانش ارسال شد')
    }

    return (
        <section id='feedback' aria-labelledby='feedback-title' className={cardStyles(false, 'relative scroll-mt-24 p-5 sm:p-6')}>
            <ConfluenceHint id='article-feedback' />

            {!entry && (
                <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='flex flex-col gap-1'>
                        <h2 id='feedback-title' className='text-h3 text-navy'>آیا این محتوا مفید بود؟</h2>
                        <p className='text-small text-muted-foreground'>
                            {formatNumber(helpfulCount)} همکار این دانش را مفید دانسته‌اند.
                        </p>
                    </div>

                    <div className='flex gap-2'>
                        <Button
                            variant='outline'
                            onClick={() => {
                                save({ value: 'yes', sent: true })
                                notify('از بازخورد شما سپاسگزاریم')
                            }}
                        >
                            <ThumbsUp aria-hidden='true' />
                            بله، مفید بود
                        </Button>

                        <Button variant='outline' onClick={() => save({ value: 'no' })}>
                            <ThumbsDown aria-hidden='true' />
                            خیر
                        </Button>
                    </div>
                </div>
            )}

            {entry?.value === 'yes' && (
                <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='flex items-center gap-3'>
                        <span className='flex size-10 items-center justify-center rounded-full bg-success-soft text-success-strong'>
                            <CircleCheck className='size-5' aria-hidden='true' />
                        </span>

                        <div className='flex flex-col'>
                            <h2 id='feedback-title' className='text-body font-bold text-navy'>از بازخورد شما سپاسگزاریم</h2>
                            <p className='text-small text-muted-foreground'>
                                شما و {formatNumber(helpfulCount - 1)} همکار دیگر این دانش را مفید دانسته‌اید.
                            </p>
                        </div>
                    </div>

                    <Button variant='ghost' size='sm' onClick={() => save(null)}>
                        تغییر پاسخ
                    </Button>
                </div>
            )}

            {entry?.value === 'no' && !entry.sent && (
                <form onSubmit={sendNegative} className='flex flex-col gap-4'>
                    <div className='flex flex-col gap-1'>
                        <h2 id='feedback-title' className='text-h3 text-navy'>چه چیزی می‌توانست این دانش را بهتر کند؟</h2>
                        <p className='text-small text-muted-foreground'>پاسخ شما مستقیم برای مالک دانش ارسال می‌شود.</p>
                    </div>

                    <div className='flex flex-wrap gap-2' role='group' aria-label='دلیل'>
                        {NEGATIVE_REASONS.map(reason => {
                            const active = reasons.includes(reason)

                            return (
                                <button
                                    key={reason}
                                    type='button'
                                    aria-pressed={active}
                                    onClick={() => setReasons(current => active ? current.filter(item => item !== reason) : [...current, reason])}
                                    className={cn(
                                        'h-9 cursor-pointer rounded-full border px-3.5 text-small transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                                        active ? 'border-primary-strong bg-primary-strong text-white' : 'border-input bg-card text-foreground hover:border-primary/40 hover:text-primary-deep'
                                    )}
                                >
                                    {reason}
                                </button>
                            )
                        })}
                    </div>

                    <Field id='feedback-note' label='توضیح بیشتر' hint='اختیاری؛ هرچه دقیق‌تر بنویسید، اصلاح سریع‌تر انجام می‌شود.'>
                        <Textarea id='feedback-note' value={note} onChange={event => setNote(event.target.value)} rows={3} placeholder='برای مثال: مقادیر زمان انتظار با پیکربندی فعلی هم‌خوان نیست.' />
                    </Field>

                    <div className='flex gap-2'>
                        <Button type='submit' disabled={reasons.length === 0 && note.trim().length === 0}>
                            <Send aria-hidden='true' />
                            ارسال بازخورد
                        </Button>

                        <Button variant='ghost' onClick={() => save(null)}>
                            انصراف
                        </Button>
                    </div>
                </form>
            )}

            {entry?.value === 'no' && entry.sent && (
                <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='flex items-center gap-3'>
                        <span className='flex size-10 items-center justify-center rounded-full bg-primary-soft text-primary-deep'>
                            <MessageSquareText className='size-5' aria-hidden='true' />
                        </span>

                        <div className='flex flex-col'>
                            <h2 id='feedback-title' className='text-body font-bold text-navy'>بازخورد شما ثبت شد</h2>
                            <p className='text-small text-muted-foreground'>
                                مالک دانش، {PERSON_BY_SLUG[article.owner]?.name}، بازخورد شما را بررسی می‌کند.
                            </p>
                        </div>
                    </div>

                    <Button variant='ghost' size='sm' onClick={() => save(null)}>
                        تغییر پاسخ
                    </Button>
                </div>
            )}
        </section>
    )
}

const SUGGESTION_TYPES = [
    { value: 'error', label: 'اصلاح خطای محتوایی' },
    { value: 'outdated', label: 'به‌روزرسانی اطلاعات قدیمی' },
    { value: 'complete', label: 'تکمیل محتوا' },
    { value: 'other', label: 'سایر موارد' }
]

interface SuggestEditProps {
    article: Article
    sections: string[]
}

export const SuggestEdit: FC<SuggestEditProps> = ({ article, sections }): ReactNode => {
    const [open, setOpen] = useState(false)
    const [kind, setKind] = useState('error')
    const [section, setSection] = useState('')
    const [text, setText] = useState('')
    const [touched, setTouched] = useState(false)
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)
    const owner = PERSON_BY_SLUG[article.owner]
    const error = touched && text.trim().length < 15 ? 'لطفاً پیشنهاد خود را دست‌کم در ۱۵ نویسه توضیح دهید.' : undefined

    const submit = (event: FormEvent) => {
        event.preventDefault()
        setTouched(true)

        if (text.trim().length < 15) return

        setSending(true)

        window.setTimeout(() => {
            setSending(false)
            setSent(true)
        }, 700)
    }

    const reset = () => {
        setKind('error')
        setSection('')
        setText('')
        setTouched(false)
        setSent(false)
        setOpen(true)
    }

    return (
        <section id='suggest' aria-labelledby='suggest-title' className={cardStyles(false, 'relative scroll-mt-24 p-5 sm:p-6')}>
            <ConfluenceHint id='article-suggest' />

            <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex items-start gap-3'>
                    <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary text-navy'>
                        <PencilLine className='size-5' aria-hidden='true' />
                    </span>

                    <div className='flex flex-col gap-1'>
                        <h2 id='suggest-title' className='text-h3 text-navy'>پیشنهاد اصلاح محتوا</h2>
                        <p className='text-small text-muted-foreground'>
                            اطلاعاتی نادرست، قدیمی یا ناقص دیدید؟ به مالک دانش اطلاع دهید.
                        </p>
                    </div>
                </div>

                {!open && !sent && (
                    <Button variant='secondary' onClick={() => setOpen(true)} aria-expanded={open} aria-controls='suggest-form'>
                        ثبت پیشنهاد اصلاح
                    </Button>
                )}
            </div>

            {sent ? (
                <Callout tone='success' title='پیشنهاد شما ثبت شد' className='mt-5'>
                    <p>
                        پیشنهاد «{SUGGESTION_TYPES.find(item => item.value === kind)?.label}» برای {owner?.name ?? 'مالک دانش'} ارسال شد و نتیجه بررسی از طریق اعلان به شما اطلاع داده می‌شود.
                    </p>

                    <button type='button' onClick={reset} className='mt-1 w-fit cursor-pointer text-small font-semibold text-primary-strong hover:text-primary-deep'>
                        ثبت پیشنهاد دیگر
                    </button>
                </Callout>
            ) : open && (
                <form id='suggest-form' onSubmit={submit} className='mt-5 flex flex-col gap-5 border-t border-border pt-5' noValidate>
                    <fieldset className='flex flex-col gap-2'>
                        <legend className='mb-1 text-sm font-semibold text-navy'>نوع پیشنهاد</legend>

                        <div className='grid gap-2 sm:grid-cols-2'>
                            {SUGGESTION_TYPES.map(item => (
                                <Check
                                    key={item.value}
                                    type='radio'
                                    name='suggestion-kind'
                                    id={`suggestion-${item.value}`}
                                    label={item.label}
                                    checked={kind === item.value}
                                    onChange={() => setKind(item.value)}
                                    className='rounded-lg border border-input px-3 py-2.5 has-checked:border-primary has-checked:bg-primary-soft/60'
                                />
                            ))}
                        </div>
                    </fieldset>

                    <Field id='suggestion-section' label='بخش مربوط' hint='اختیاری'>
                        <Select id='suggestion-section' value={section} onChange={event => setSection(event.target.value)}>
                            <option value=''>کل محتوا</option>

                            {sections.map(item => (
                                <option key={item} value={item}>{item}</option>
                            ))}
                        </Select>
                    </Field>

                    <Field id='suggestion-text' label='شرح پیشنهاد' required error={error} counter={{ value: text.length, max: 1000 }}>
                        <Textarea
                            id='suggestion-text'
                            value={text}
                            maxLength={1000}
                            invalid={Boolean(error)}
                            aria-describedby={error ? 'suggestion-text-error' : undefined}
                            onChange={event => setText(event.target.value)}
                            onBlur={() => setTouched(true)}
                            rows={4}
                            placeholder='چه چیزی باید اصلاح شود و منبع اطلاعات درست کدام است؟'
                        />
                    </Field>

                    <div className='flex gap-2'>
                        <Button type='submit' loading={sending}>
                            {!sending && <Send aria-hidden='true' />}
                            ارسال برای مالک دانش
                        </Button>

                        <Button variant='ghost' onClick={() => setOpen(false)} disabled={sending}>
                            انصراف
                        </Button>
                    </div>
                </form>
            )}
        </section>
    )
}

type LocalComment = ArticleComment & { fresh?: boolean }

interface CommentItemProps {
    comment: LocalComment
    onReply?: (text: string) => void
    nested?: boolean
}

const CommentItem: FC<CommentItemProps> = ({ comment, onReply, nested = false }): ReactNode => {
    const author = PERSON_BY_SLUG[comment.author]
    const [replying, setReplying] = useState(false)
    const [reply, setReply] = useState('')

    return (
        <li className={cn('flex gap-3', nested && 'ms-6 border-s-2 border-border ps-4 sm:ms-12')}>
            <Avatar name={author?.name ?? 'همکار'} tone={author?.tone ?? 4} size={nested ? 'sm' : 'md'} />

            <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                <div className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
                    {author ? (
                        <Link href={`/people/${author.slug}`} className='text-sm font-bold text-navy hover:text-primary-deep'>{author.name}</Link>
                    ) : (
                        <span className='text-sm font-bold text-navy'>همکار</span>
                    )}

                    {author && <span className='text-caption text-muted-foreground'>{author.role}</span>}

                    <span className='text-caption text-subtle'>· {comment.fresh ? 'همین حالا' : formatJalali(comment.date)}</span>
                </div>

                <p className='whitespace-pre-line text-body text-foreground'>{comment.text}</p>

                {onReply && !replying && (
                    <button type='button' onClick={() => setReplying(true)} className='flex w-fit cursor-pointer items-center gap-1 text-small font-semibold text-muted-foreground transition-colors hover:text-primary-strong'>
                        <Reply className='size-4' aria-hidden='true' />
                        پاسخ
                    </button>
                )}

                {onReply && replying && (
                    <form
                        className='mt-1 flex flex-col gap-2'
                        onSubmit={event => {
                            event.preventDefault()

                            if (!reply.trim()) return

                            onReply(reply.trim())
                            setReply('')
                            setReplying(false)
                        }}
                    >
                        <label htmlFor={`reply-${comment.id}`} className='sr-only'>پاسخ به {author?.name}</label>
                        <Textarea id={`reply-${comment.id}`} value={reply} onChange={event => setReply(event.target.value)} rows={2} placeholder='پاسخ خود را بنویسید...' autoFocus />

                        <div className='flex gap-2'>
                            <Button type='submit' size='sm' disabled={!reply.trim()}>ارسال پاسخ</Button>
                            <Button size='sm' variant='ghost' onClick={() => setReplying(false)}>انصراف</Button>
                        </div>
                    </form>
                )}
            </div>
        </li>
    )
}

interface ArticleCommentsProps {
    initial: ArticleComment[]
}

export const ArticleComments: FC<ArticleCommentsProps> = ({ initial }): ReactNode => {
    const [comments, setComments] = useState<LocalComment[]>(initial)
    const [text, setText] = useState('')
    const counter = useRef(0)
    const { notify } = useToast()
    const total = comments.reduce((sum, comment) => sum + 1 + (comment.replies?.length ?? 0), 0)

    const nextId = () => {
        counter.current += 1

        return `local-${counter.current}`
    }

    const addComment = (event: FormEvent) => {
        event.preventDefault()

        if (!text.trim()) return

        const comment: LocalComment = { id: nextId(), author: CURRENT_USER.slug, date: '', text: text.trim(), fresh: true }

        setComments(current => [...current, comment])
        setText('')
        notify('نظر شما ثبت شد')
    }

    const addReply = (parentId: string, value: string) => {
        const reply: LocalComment = { id: nextId(), author: CURRENT_USER.slug, date: '', text: value, fresh: true }

        setComments(current => current.map(comment => comment.id === parentId
            ? { ...comment, replies: [...(comment.replies ?? []), reply] }
            : comment
        ))
        notify('پاسخ شما ثبت شد')
    }

    return (
        <section id='comments' aria-labelledby='comments-title' className={cardStyles(false, 'relative scroll-mt-24 p-5 sm:p-6')}>
            <ConfluenceHint id='article-comments' />

            <h2 id='comments-title' className='flex items-center gap-2 text-h3 text-navy'>
                <MessageSquare className='size-5 text-primary' aria-hidden='true' />
                نظرات و بازخورد همکاران
                <span className='rounded-full bg-secondary px-2 text-caption font-semibold tabular-nums text-muted-foreground'>{toFaDigits(total)}</span>
            </h2>

            {comments.length === 0 ? (
                <EmptyState compact icon={MessageSquareText} title='هنوز نظری ثبت نشده است' description='نخستین دیدگاه، تجربه یا پرسش خود را درباره این دانش بنویسید.' />
            ) : (
                <ul className='mt-5 flex flex-col gap-5'>
                    {comments.map(comment => (
                        <li key={comment.id} className='flex flex-col gap-4'>
                            <ul className='flex flex-col gap-4'>
                                <CommentItem comment={comment} onReply={value => addReply(comment.id, value)} />

                                {comment.replies?.map(reply => (
                                    <CommentItem key={reply.id} comment={reply} nested />
                                ))}
                            </ul>
                        </li>
                    ))}
                </ul>
            )}

            <form onSubmit={addComment} className='mt-6 flex gap-3 border-t border-border pt-5'>
                <Avatar name={CURRENT_USER.name} tone={CURRENT_USER.tone} />

                <div className='flex flex-1 flex-col gap-2'>
                    <label htmlFor='new-comment' className='sr-only'>نظر شما</label>
                    <Textarea id='new-comment' value={text} onChange={event => setText(event.target.value)} rows={3} placeholder='دیدگاه، تجربه یا پرسش خود را درباره این دانش بنویسید...' />

                    <Button type='submit' className='w-fit' disabled={!text.trim()}>
                        <Send aria-hidden='true' />
                        ثبت نظر
                    </Button>
                </div>
            </form>
        </section>
    )
}
